import { ShiftType } from '@/types/template';

import { backendRequest } from './http-client';

interface ShiftTypeResponse {
  id: string;
  name: string;
  depth: number;
  concatenatedName?: string;
  parent?: {
    id: string;
    name: string;
  };
}

/**
 * Lists all shift types from the backend API for the current tenant.
 *
 * @throws Error if the API call fails
 */
export async function listShiftTypes(): Promise<ShiftType[]> {
  const rows = await backendRequest<ShiftTypeResponse[]>(
    '/v1/shift-types',
    'Failed to list shift types',
  );
  return sortShiftTypesIntoTree(rows.map(mapShiftTypeResponseToShiftType));
}

/**
 * Sorts a flat list of shift types into depth-first tree order so that
 * children appear directly after their parent. Root items (depth 0) are
 * sorted alphabetically by name; children within the same parent are also
 * sorted alphabetically. Items whose parent is not present in the list
 * are treated as roots.
 */
export function sortShiftTypesIntoTree(shiftTypes: ShiftType[]): ShiftType[] {
  const childrenByParentId = new Map<string, ShiftType[]>();
  const roots: ShiftType[] = [];

  for (const st of shiftTypes) {
    if (st.parent) {
      const siblings = childrenByParentId.get(st.parent.id);
      if (siblings) {
        siblings.push(st);
      } else {
        childrenByParentId.set(st.parent.id, [st]);
      }
    } else {
      roots.push(st);
    }
  }

  const alphabetical = (a: ShiftType, b: ShiftType) => a.name.localeCompare(b.name);
  roots.sort(alphabetical);
  for (const children of childrenByParentId.values()) {
    children.sort(alphabetical);
  }

  const result: ShiftType[] = [];

  const appendSubtree = (node: ShiftType) => {
    result.push(node);
    const children = childrenByParentId.get(node.id);
    if (children) {
      for (const child of children) {
        appendSubtree(child);
      }
    }
  };

  // Append roots that have children in the map, plus any "virtual" parents
  // that only exist as parent references (depth 0 items already in roots).
  // Virtual parents are parent IDs referenced by children but not present
  // as their own entry; we still need to emit their subtrees.
  const rootIds = new Set(roots.map((r) => r.id));
  for (const root of roots) {
    appendSubtree(root);
  }

  // Emit orphaned subtrees whose parent is not in the flat list
  for (const [parentId, children] of childrenByParentId) {
    if (!rootIds.has(parentId) && !shiftTypes.some((st) => st.id === parentId)) {
      for (const child of children) {
        appendSubtree(child);
      }
    }
  }

  return result;
}

/**
 * Converts a shift-type API response into the UI `ShiftType` shape. The
 * rotagen boundary keys shift types by `id`, which the backend uses
 * verbatim from Rotaready.
 */
export function mapShiftTypeResponseToShiftType(response: ShiftTypeResponse): ShiftType {
  return {
    id: response.id,
    name: response.name,
    depth: response.depth,
    ...(response.concatenatedName ? { concatenatedName: response.concatenatedName } : {}),
    ...(response.parent ? { parent: response.parent } : {}),
  };
}
